import React from "react";
import { AppBar, Toolbar, IconButton, Typography, Box, Container, useTheme, useMediaQuery } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import BottomNavBar from "./BottomNavBar";

/*
Shell of every mobile page opened from another page (profile, password,
notifications, campaign detail…): sticky app bar with "back" + title, a
centered `md` container and the BottomNavBar. Top-level pages (the bottom
nav tabs: Leads, Clients, Metrics, Settings) use MainLayout instead.
*/

// Fine-pointer/hover capability identifies a desktop (mouse-driven) device
// regardless of how narrow the browser window is resized — unlike a
// viewport-width breakpoint, which would (wrongly) turn off below `md`.
const SubPageLayout = ({ title, onBack, children }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDesktopDevice = useMediaQuery("(pointer: fine) and (hover: hover)");

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Toolbar>
          <IconButton edge="start" aria-label={t("back")} onClick={onBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="h1" sx={{ flex: 1 }}>
            {title}
          </Typography>
        </Toolbar>
      </AppBar>

      <Container
        maxWidth="md"
        sx={{
          py: { xs: 3, md: 4 },
          // Reserves space for the fixed BottomNavBar on mobile so it doesn't
          // cover the last card (BottomNavBar hides itself at md+).
          pb: { xs: 11, md: 4 },
          // min() caps the forced width at 100% so a narrow (but mouse-driven)
          // browser window — e.g. a desktop resized down, or this same headless
          // browser — never gets a Container wider than the viewport itself.
          ...(isDesktopDevice && { minWidth: `min(${theme.breakpoints.values.sm}px, 100%)` }),
        }}
      >
        {children}
      </Container>

      <BottomNavBar />
    </Box>
  );
};

export default SubPageLayout;
